
import React, { useState } from 'react';
import { Home, Briefcase, Utensils, AlertTriangle, Plus, X, MessageCircle, HandHeart, Search, Filter } from 'lucide-react';
import { Cause } from '../types';
import { useAppContext } from '../App';
import { checkContentSafety } from '../services/geminiService';
import { Link } from 'react-router-dom';

const Volunteering: React.FC = () => {
    const { causes, currentUser, addCause, allUsers, t } = useAppContext();
    
    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState<'REQUEST' | 'OFFER'>('REQUEST');
    
    // Search State
    const [searchQuery, setSearchQuery] = useState('');

    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState<{
        title: string;
        description: string;
        type: 'FOOD' | 'HOUSING' | 'JOB' | 'OTHER';
        urgency: 'LOW' | 'MEDIUM' | 'HIGH';
        contactInfo: string;
    }>({
        title: '',
        description: '',
        type: 'OTHER',
        urgency: 'LOW',
        contactInfo: ''
    });

    const openModal = (type: 'REQUEST' | 'OFFER') => {
        setModalType(type);
        setFormData({ title: '', description: '', type: 'OTHER', urgency: 'LOW', contactInfo: '' });
        setShowModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!currentUser) return;
        setError(null);
        setIsSubmitting(true);

        // Safety Check
        const safetyCheck = await checkContentSafety(`${formData.title} ${formData.description}`);
        if (!safetyCheck.safe) {
            setError(`Request flagged: ${safetyCheck.reason}`);
            setIsSubmitting(false);
            return;
        }

        const newCause: Cause = {
            id: Date.now().toString(), // Temp ID until DB confirms
            title: formData.title,
            description: formData.description,
            type: formData.type,
            urgency: formData.urgency,
            contactInfo: formData.contactInfo,
            authorId: currentUser.id,
            category: modalType
        };

        await addCause(newCause);
        setShowModal(false);
        setIsSubmitting(false);
    };

    // Filter causes based on search query
    const filteredCauses = causes.filter(c => {
        const matchesSearch = c.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                              c.description.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-orange-400 to-pink-500 rounded-lg shadow p-8 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 p-10 opacity-10">
                    <HandHeart className="w-48 h-48 text-white" />
                </div>
                <div className="relative z-10">
                    <h1 className="text-3xl font-bold mb-2">{t('acts_of_service')}</h1>
                    <p className="opacity-90 max-w-2xl text-lg mb-6">
                        "For I was hungry and you gave me something to eat, I was thirsty and you gave me something to drink, I was a stranger and you invited me in." — Matthew 25:35
                    </p>
                    
                    {/* Search Bar */}
                    <div className="bg-white/20 backdrop-blur-md rounded-lg p-2 max-w-xl flex items-center gap-3 border border-white/30 mb-6">
                        <Search className="w-5 h-5 text-white ml-2" />
                        <input 
                            type="text" 
                            placeholder="Search for needs (e.g., 'Food', 'Ride') or offers..." 
                            className="bg-transparent border-none text-white placeholder-white/70 w-full focus:outline-none"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <button 
                            onClick={() => openModal('REQUEST')}
                            className="bg-white text-orange-600 px-5 py-2.5 rounded-lg font-bold shadow-lg hover:bg-gray-50 flex items-center gap-2 transition transform hover:scale-105"
                        >
                            <AlertTriangle className="w-5 h-5"/> {t('i_need_help')}
                        </button>
                        <button 
                            onClick={() => openModal('OFFER')}
                            className="bg-blue-600 text-white border border-blue-500 px-5 py-2.5 rounded-lg font-bold shadow-lg hover:bg-blue-700 flex items-center gap-2 transition transform hover:scale-105"
                        >
                            <HandHeart className="w-5 h-5"/> {t('i_want_help')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid gap-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-800">{t('community_board')}</h2>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">{filteredCauses.length} items</span>
                </div>

                {filteredCauses.map(cause => {
                    const author = allUsers.find(u => u.id === cause.authorId);
                    const isRequest = cause.category === 'REQUEST';
                    const isUrgent = cause.urgency === 'HIGH';

                    return (
                        <div key={cause.id} className={`bg-white rounded-lg shadow p-6 flex flex-col sm:flex-row gap-4 border-l-4 ${isRequest ? 'border-orange-400' : 'border-blue-500'}`}>
                            <div className={`p-3 rounded-full h-fit w-fit ${isRequest ? 'bg-orange-50' : 'bg-blue-50'}`}>
                                {cause.type === 'HOUSING' && <Home className={`w-6 h-6 ${isRequest ? 'text-orange-600' : 'text-blue-600'}`} />}
                                {cause.type === 'FOOD' && <Utensils className={`w-6 h-6 ${isRequest ? 'text-orange-600' : 'text-blue-600'}`} />}
                                {cause.type === 'JOB' && <Briefcase className={`w-6 h-6 ${isRequest ? 'text-orange-600' : 'text-blue-600'}`} />}
                                {cause.type === 'OTHER' && <AlertTriangle className={`w-6 h-6 ${isRequest ? 'text-orange-600' : 'text-blue-600'}`} />}
                            </div>
                            <div className="flex-1">
                                <div className="flex flex-wrap justify-between items-start gap-2">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-lg font-bold text-gray-900">{cause.title}</h3>
                                        <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase ${isRequest ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                                            {isRequest ? 'Asking For' : 'Offering'}
                                        </span>
                                    </div>
                                    {isUrgent && isRequest && (
                                        <span className="flex items-center gap-1 text-xs font-bold text-red-600 bg-red-100 px-2 py-1 rounded animate-pulse">
                                            <AlertTriangle className="w-3 h-3" /> {t('urgent')}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-500 mt-1 mb-2">Posted by <Link to={`/user/${cause.authorId}`} className="font-semibold hover:underline text-blue-600">{author?.name || 'Unknown'}</Link></p>
                                <p className="text-gray-600 mb-4">{cause.description}</p>
                                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50 p-3 rounded-lg">
                                    <div className="text-sm text-gray-700">
                                        <span className="font-bold text-xs text-gray-500 uppercase mr-2">{t('contact_info')}:</span>
                                        {cause.contactInfo}
                                    </div>
                                    <Link to={`/user/${cause.authorId}`} className="text-blue-600 font-bold hover:underline flex items-center gap-2 text-sm">
                                        <MessageCircle className="w-4 h-4"/> {t('connect')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {filteredCauses.length === 0 && (
                    <div className="text-center py-12 bg-white rounded-lg shadow border border-gray-100">
                        <p className="text-gray-500">No results found matching "{searchQuery}".</p>
                    </div>
                )}
            </div>

            {/* Create Modal (Handles both Requests and Offers) */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 animate-fadeIn">
                        <div className="flex justify-between items-center mb-6 border-b pb-4">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">
                                    {modalType === 'REQUEST' ? t('create_request') : t('create_offer')}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    {modalType === 'REQUEST' ? 'Let the community know what you need.' : 'Share what skills or resources you can provide.'}
                                </p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X /></button>
                        </div>
                        
                        {error && <div className="mb-4 text-xs text-red-600 bg-red-50 p-2 rounded flex items-center gap-1"><AlertTriangle className="w-4 h-4"/> {error}</div>}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input 
                                    required
                                    type="text" 
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                    placeholder={modalType === 'REQUEST' ? "e.g. Need food assistance" : "e.g. Plumbing services, Canned food"}
                                    value={formData.title}
                                    onChange={e => setFormData({...formData, title: e.target.value})}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                    <select 
                                        className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                        value={formData.type}
                                        onChange={e => setFormData({...formData, type: e.target.value as any})}
                                    >
                                        <option value="FOOD">Food</option>
                                        <option value="HOUSING">Housing</option>
                                        <option value="JOB">Job / Work</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{modalType === 'REQUEST' ? 'Urgency' : 'Availability'}</label>
                                    <select 
                                        className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                        value={formData.urgency}
                                        onChange={e => setFormData({...formData, urgency: e.target.value as any})}
                                    >
                                        <option value="LOW">Low</option>
                                        <option value="MEDIUM">Medium</option>
                                        <option value="HIGH">High</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact_info')}</label>
                                <input 
                                    required
                                    type="text" 
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                    placeholder="Phone number or Email"
                                    value={formData.contactInfo}
                                    onChange={e => setFormData({...formData, contactInfo: e.target.value})}
                                />
                                <p className="text-xs text-gray-500 mt-1">Visible to all members.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea 
                                    required
                                    className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors h-24 resize-none"
                                    placeholder={modalType === 'REQUEST' ? "Describe your situation..." : "Describe what you can offer..."}
                                    value={formData.description}
                                    onChange={e => setFormData({...formData, description: e.target.value})}
                                />
                            </div>

                            <button 
                                type="submit" 
                                disabled={isSubmitting}
                                className={`w-full text-white py-3 rounded-lg font-bold mt-4 disabled:opacity-50 ${modalType === 'REQUEST' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-600 hover:bg-blue-700'}`}
                            >
                                {isSubmitting ? t('posting') : modalType === 'REQUEST' ? t('post') : t('post')}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Volunteering;
